import { GlassCard } from '@/components/ui/layout/card';
import {
  CardHeader,
  CardContent,
  CardTitle,
} from '@/components/ui/layout/card';

export default function Privacy() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-primary">Privacy Policy</h1>
          <p className="text-muted-foreground mt-2">AthleteSpace</p>
        </div>

        {/* Privacy Policy Content */}
        <GlassCard>
          <CardHeader>
            <CardTitle>Privacy Policy – AthleteSpace</CardTitle>
            <p className="text-sm text-muted-foreground mt-2">Last updated: March 2026</p>
          </CardHeader>
          <CardContent className="space-y-8">
            <div className="prose prose-sm max-w-none dark:prose-invert">
              {/* Introduction */}
              <section className="mb-6">
                <p className="text-muted-foreground leading-relaxed">
                  AthleteSpace ("we", "our", "us") is a fitness and performance application designed to provide training insights to endurance athletes. We respect your privacy and are committed to protecting your personal data. This policy explains what data we collect, how we use it, and your rights.
                </p>
              </section>

              {/* Section 1 */}
              <section>
                <h2 className="text-xl font-semibold text-primary mb-4">1. Data We Collect</h2>
                <p className="text-muted-foreground leading-relaxed mb-3">
                  With your explicit consent, AthleteSpace may collect the following categories of data:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  <li><strong>Account information:</strong> email address, name, and authentication provider (Google, Apple)</li>
                  <li><strong>Activity data:</strong> workouts, duration, distance, and pace from connected services</li>
                  <li><strong>Health and fitness data:</strong> heart rate, active energy burned, and running/cycling distance from Apple Health (read-only access)</li>
                  <li><strong>Training preferences:</strong> goals, availability, sport type, and injury information provided during onboarding</li>
                  <li><strong>Voice data:</strong> audio from voice coaching sessions, processed in real time and not stored after the session ends</li>
                </ul>
              </section>

              {/* Section 2 */}
              <section>
                <h2 className="text-xl font-semibold text-primary mb-4">2. How We Use Your Data</h2>
                <p className="text-muted-foreground leading-relaxed mb-3">
                  We use collected data solely to:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  <li>Provide personalized training insights and performance analysis</li>
                  <li>Generate recovery and fatigue trends</li>
                  <li>Power the AI coaching features, including training plan generation and voice coaching</li>
                  <li>Improve the functionality and accuracy of the application</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  AthleteSpace does not provide medical diagnosis or treatment. All AI-generated recommendations are informational and should not replace professional medical advice.
                </p>
              </section>

              {/* Section 3 — AI & Automated Decisions */}
              <section>
                <h2 className="text-xl font-semibold text-primary mb-4">3. AI and Automated Decision-Making</h2>
                <p className="text-muted-foreground leading-relaxed mb-3">
                  AthleteSpace uses artificial intelligence provided by <strong>OpenAI</strong> to generate training plans, analyze performance, and provide coaching recommendations. When you use AI coaching features, the following data may be sent to OpenAI for processing:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  <li>Your training profile (sport, goals, availability, experience level)</li>
                  <li>Recent workout history and performance metrics</li>
                  <li>Fitness and fatigue indicators (training load, CTL, ATL)</li>
                  <li>Messages you send to the AI coach</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  This data is sent to OpenAI's API solely for generating your coaching response. OpenAI processes this data under a data processing agreement and does not use it to train their models. Data is encrypted in transit and is not retained by OpenAI beyond the duration of the API request.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  You will be asked for explicit consent before any data is shared with OpenAI. You can revoke this consent at any time in Settings &gt; Privacy &amp; Security, which will disable AI coaching features.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  These AI features:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  <li>Are informational decision-support tools, not a substitute for professional coaching</li>
                  <li>Do not make binding decisions about your health or training</li>
                  <li>Can be overridden — you retain full control over your training schedule</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  Voice coaching sessions are processed in real time using OpenAI's API. Audio is streamed for transcription and response generation only — it is not recorded or stored by AthleteSpace.
                </p>
              </section>

              {/* Section 4 */}
              <section>
                <h2 className="text-xl font-semibold text-primary mb-4">4. Data Sharing</h2>
                <p className="text-muted-foreground leading-relaxed mb-3">
                  We do not sell, rent, or share individual user data with advertisers or third parties for marketing purposes.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-3">
                  We share data with the following third-party service providers strictly for app functionality:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  <li><strong>OpenAI</strong> — processes your training data and messages to power AI coaching features (training plans, performance analysis, voice coaching). Data is sent only with your explicit consent and is not retained by OpenAI beyond request processing.</li>
                  <li><strong>Authentication providers</strong> (Google, Apple) — for secure sign-in only.</li>
                  <li><strong>Fitness platforms</strong> (Strava, Garmin) — to sync workout data, only when you connect these services.</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  These providers process data under contract and do not retain it beyond the scope of service delivery. Aggregated and anonymized data may be used internally for product improvement.
                </p>
              </section>

              {/* Section 5 */}
              <section>
                <h2 className="text-xl font-semibold text-primary mb-4">5. Data Storage and Transfers</h2>
                <p className="text-muted-foreground leading-relaxed mb-3">
                  Your data is stored on secure servers in the United States. If you are located outside the United States, your data will be transferred to and processed in the United States. We implement appropriate safeguards including encryption in transit and at rest.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Apple Health data is read on your device and transmitted to our servers over encrypted connections. Voice audio is streamed directly to AI processing services and is not persistently stored.
                </p>
              </section>

              {/* Section 6 */}
              <section>
                <h2 className="text-xl font-semibold text-primary mb-4">6. Data Retention</h2>
                <p className="text-muted-foreground leading-relaxed mb-3">
                  We retain your personal data for as long as your account is active. When you delete your account:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  <li>All personal data, activities, training plans, and preferences are permanently deleted</li>
                  <li>Deletion is immediate and irreversible</li>
                  <li>Third-party service connections (Strava, Garmin) are unlinked</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  You can export your data in JSON or CSV format before deleting your account via Settings &gt; Data Management.
                </p>
              </section>

              {/* Section 7 */}
              <section>
                <h2 className="text-xl font-semibold text-primary mb-4">7. Data Security</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We implement reasonable technical and organizational safeguards to protect your data, including encrypted storage, secure communication protocols (HTTPS/TLS), and secure authentication via industry-standard OAuth providers.
                </p>
              </section>

              {/* Section 8 */}
              <section>
                <h2 className="text-xl font-semibold text-primary mb-4">8. Your Rights</h2>
                <p className="text-muted-foreground leading-relaxed mb-3">
                  Depending on your jurisdiction, you may have the following rights regarding your personal data:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  <li><strong>Access:</strong> Request a copy of your data (available via Settings &gt; Data Management &gt; Export)</li>
                  <li><strong>Deletion:</strong> Delete your account and all associated data (available via Settings &gt; Privacy &amp; Security)</li>
                  <li><strong>Correction:</strong> Update your personal information through the app settings</li>
                  <li><strong>Portability:</strong> Export your data in a machine-readable format (JSON or CSV)</li>
                  <li><strong>Revoke consent:</strong> Disconnect integrations or delete your account at any time</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  You control which services you connect and may revoke access at any time through Settings &gt; Integrations.
                </p>
              </section>

              {/* Section 9 */}
              <section>
                <h2 className="text-xl font-semibold text-primary mb-4">9. Apple Health Data</h2>
                <p className="text-muted-foreground leading-relaxed mb-3">
                  AthleteSpace requests read-only access to the following Apple Health data types, with your explicit permission:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  <li>Workouts (all activity types)</li>
                  <li>Heart rate</li>
                  <li>Active energy burned</li>
                  <li>Running distance</li>
                  <li>Cycling distance</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  This data is synced to AthleteSpace and used exclusively to provide training insights and performance analysis. When you have consented to AI coaching, Apple Health data may be included in the context sent to OpenAI for generating personalized recommendations. We do not write data to Apple Health. Apple Health data is never sold, used for advertising, or shared with third parties beyond what is described in Section 4. You can disconnect Apple Health at any time via Settings &gt; Integrations.
                </p>
              </section>

              {/* Section 10 */}
              <section>
                <h2 className="text-xl font-semibold text-primary mb-4">10. Third-Party Services</h2>
                <p className="text-muted-foreground leading-relaxed">
                  AthleteSpace integrates with third-party fitness platforms (Strava, Garmin, Apple Health) only with your explicit authorization and in accordance with their respective privacy policies. You can disconnect any integration at any time.
                </p>
              </section>

              {/* Section 11 */}
              <section>
                <h2 className="text-xl font-semibold text-primary mb-4">11. Children's Privacy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  AthleteSpace is intended for users aged 13 and older. We do not knowingly collect personal data from children under 13. If you believe a child under 13 has provided us with personal data, please contact us so we can delete it.
                </p>
              </section>

              {/* Section 12 */}
              <section>
                <h2 className="text-xl font-semibold text-primary mb-4">12. Changes to This Policy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  This policy may be updated as the product evolves. Material changes will be communicated via in-app notification or email. The "Last updated" date at the top reflects the most recent revision.
                </p>
              </section>

              {/* Section 13 */}
              <section>
                <h2 className="text-xl font-semibold text-primary mb-4">13. Contact</h2>
                <p className="text-muted-foreground leading-relaxed">
                  For privacy-related questions or to exercise your data rights, contact us at <a href="mailto:privacy@athletespace.ai" className="text-accent hover:underline">privacy@athletespace.ai</a>.
                </p>
              </section>
            </div>
          </CardContent>
        </GlassCard>
      </div>
  );
}
